"use client";

import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import React, { useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

export function MagicCard({
    children,
    className,
    gradientSize = 200,
    gradientColor = "#262626",
    gradientOpacity = 0.8,
    gradientFrom = "#EA580C", // Brand Orange
    gradientTo = "#fb923c", // Lighter Orange
}) {
    const cardRef = useRef(null);
    const mouseX = useMotionValue(-gradientSize);
    const mouseY = useMotionValue(-gradientSize);

    const handleMouseMove = useCallback(
        (e) => {
            if (cardRef.current) {
                const { left, top } = cardRef.current.getBoundingClientRect();
                mouseX.set(e.clientX - left);
                mouseY.set(e.clientY - top);
            }
        },
        [mouseX, mouseY]
    );

    // Reset mouse position when leaving card
    const handleMouseLeave = useCallback(() => {
        mouseX.set(-gradientSize);
        mouseY.set(-gradientSize);
    }, [gradientSize, mouseX, mouseY]);

    useEffect(() => {
        // Add global listener if we wanted it to follow strictly or just rely on onMouseMove prop
        // The provided example usually puts listeners on the card itself.
    }, []);

    const background = useMotionTemplate`
        radial-gradient(
          ${gradientSize}px circle at ${mouseX}px ${mouseY}px,
          ${gradientColor},
          transparent 100%
        )
      `;

    return (
        <div
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={cn(
                "group relative flex w-full h-full overflow-hidden rounded-xl bg-card/60 text-card-foreground backdrop-blur-md border border-white/10",
                className
            )}
        >
            <div className="relative z-10 w-full h-full">{children}</div>
            {/* Mobile-only static gradient for visual effects */}
            <div
                className="absolute inset-0 z-0 opacity-20 bg-gradient-to-br from-orange-500/30 to-transparent md:hidden pointer-events-none"
            />
            <motion.div
                className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                    background,
                    opacity: gradientOpacity,
                }}
            />
            <motion.div
                className="pointer-events-none absolute inset-0 z-0 rounded-xl transition-opacity duration-300 group-hover:opacity-100"
                style={{
                    background,
                    opacity: 1,
                    mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    maskComposite: "exclude",
                    WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                    WebkitMaskComposite: "xor",
                    padding: "1px",
                }}
            />
            {/* Background layer to ensure text readability if needed, usually managed by parent Card bg*/}
        </div>
    );
}
